import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Alert, AlertTitle, useTheme, Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { SummaryValidation } from '@components/prereg_validation/summary_validation'
import { useBookingContext } from '@providers/booking'
import { useSessionContext } from '@providers/session'
import { isUserLoggedIn } from '@providers/session/helpers'
import { MAX_WIDGET_SIZE } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import {
  allowNoPayments,
  FlexCardDisplay,
  getOrderPrimaryServiceTypeName,
  getPoliciesData,
  InvoiceCard,
  isVisitOrder,
  PurchaseReceipt,
  PurchaseReceiptSkeleton,
  Spacer,
  SurchargePolicy,
} from 'components-ui'
import TagManager from 'react-gtm-module'
import { Hooks, Types } from 'service-api'
import { PurchaseSummarySkeleton } from '../purchase_summary/purchase_summary_skeleton'
import { useLocationContext } from '@providers/location'

const getPurchaseSummaryTitle = (serviceTypeName?: string) => {
  switch (serviceTypeName) {
    case 'grooming':
      return 'paymentSummary.title.grooming'
    case 'packages':
      return 'paymentSummary.title.packages'
    default:
      return 'paymentSummary.title'
  }
}

export const BookingConfirmation = () => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName = '', orderId = '' } = useParams()
  const theme = useTheme()
  const { location } = useLocationContext()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const [order, setOrder] = useState<Types.OrderClientDto | undefined>()
  const isMobile = renderStyle === 'mobile'
  const serviceTypeName = getOrderPrimaryServiceTypeName(order) || ''
  const isGrooming = serviceTypeName === 'grooming'
  const isVisit = isVisitOrder(order)

  useEffect(() => {
    document.title = t('title.bookingConfirmation')
  }, [])

  const receipt = Hooks.useClientReceipt(accountName, orderId)
  const orderDto = Hooks.useClientOrder(accountName, orderId)
  const bookingConfs = Hooks.useClientBookingConfigs(accountName, order?.primaryServiceType?.serviceType?.name || '', {
    enabled: isVisit,
  })
  const hideRefundPolicy = !isVisit || allowNoPayments(order?.primaryServiceType?.locationBookingConf?.[0])
  const policyCardsData = useMemo(
    () => (receipt.isSuccess ? getPoliciesData(receipt.data, hideRefundPolicy) : []),
    [receipt, hideRefundPolicy]
  )

  const loggedIn = isUserLoggedIn(sessionCtx.state.user)

  useEffect(() => {
    if (!orderDto.isSuccess) {
      return
    }

    if (!loggedIn) {
      setTimeout(
        () =>
          navigateRoute(
            'signIn',
            {},
            {
              state: {
                redirect: {
                  route: 'paymentSummary',
                  params: { orderId },
                },
                email: orderDto.data.userData?.[0]?.email,
                userExists: !!(orderDto.data.ownerId || orderDto.data.owner?.id),
              },
            }
          ),
        0
      )
      return
    }

    setOrder(orderDto.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDto.data, orderDto.isSuccess, loggedIn, sessionCtx.state.user?.email])

  useEffect(() => {
    if (orderDto.error) {
      setTimeout(() => navigateRoute('notFound', {}), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDto.error])

  useEffect(() => {
    bookingCtx.dispatch({
      type: 'SET_SEARCH_STATE',
      payload: { value: 'paymentSummary' },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  const firePurchaseEvent = (order: Types.OrderClientDto) => {
    const items =
      order.invoices?.flatMap((invoice, index) =>
        invoice.items?.map((invoiceItem) => ({
          item_id: invoiceItem.offer?.id || invoiceItem.offerId || '',
          item_name: invoiceItem.displayName || '',
          affiliation: location?.displayName || '',
          index,
          item_category: invoice.locationServiceType?.serviceType?.name || '',
          item_category2: invoiceItem.locationSpecies?.species?.name || '',
          item_category3: invoiceItem.offerType,
          price: invoiceItem.subtotal || 0,
          quantity: invoiceItem.qty || 0,
        }))
      ) || []

    const purchaseData = {
      transaction_id: order.id || '',
      value: order.invoices?.reduce((sum, invoice) => sum + (invoice.total || 0), 0) || 0,
      tax: order.invoices?.reduce((sum, invoice) => sum + (invoice.tax || 0), 0) || 0,
      currency: order.payments?.[0].currency || '',
      items,
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'purchase',
        ecommerce: purchaseData,
      },
    })
  }

  // Fire purchase event when order is successfully fetched
  useEffect(() => {
    if (orderDto.data && orderDto.isSuccess && location && bookingCtx.state.shouldFireGAPurchaseEvent) {
      firePurchaseEvent(orderDto.data)

      bookingCtx.dispatch({
        type: 'SET_FIRE_PURCHASE_EVENT',
        payload: { value: false },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDto, orderDto.isSuccess, location])

  if (orderDto.isLoading || receipt.isLoading || bookingConfs.isFetching) {
    return <PurchaseSummarySkeleton />
  }

  if (!order?.invoices) {
    return null
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto', mb: 4 }}>
      <Spacer height={3} />
      <Stack alignItems={'center'}>
        <CheckCircleIcon fontSize="large" sx={{ color: theme.palette.success.light, mb: 1 }} />
        <Typography variant="h1">{t(getPurchaseSummaryTitle(serviceTypeName))}</Typography>
      </Stack>

      <Spacer height={3} />
      {isVisit && <SummaryValidation orderId={order.id || ''} />}
      <Spacer height={3} />

      <Stack direction="column" spacing={3} flex={1} width="100%">
        {order.invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            order={order}
            onCancelInvoice={() => {
              orderDto.refetch()
              receipt.refetch()
            }}
          />
        ))}
      </Stack>

      {/* {isIntakeEnabled(bookingConfs.data?.locationBookingConf) && (
        <>
          <Spacer height={3} />
          <Intake
            flexBookingConfs={bookingConfs.data?.flexBookingConfs || []}
            order={order || {}}
            onSave={(newOrder) => {
              setOrder(newOrder)
            }}
          />
        </>
      )} */}

      <Spacer height={3} />

      <Typography variant="h2">{t('paymentSummary.subtitle')}</Typography>
      <Spacer height={2} />
      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3} flex={1}>
        <Stack spacing={3} sx={{ flex: 1 }} width="100%">
          <Box>
            {isGrooming && (
              <Alert severity="info">
                <AlertTitle>{t('paymentForm.groomRequestRefund')}</AlertTitle>
                {t('paymentForm.groomRequestNotice')}
              </Alert>
            )}
            {policyCardsData.map((group, index) => (
              <Fragment key={index}>
                <FlexCardDisplay cardData={group} />
                <Spacer height={2} />
              </Fragment>
            ))}
          </Box>
          <Box>{bookingConfs.isSuccess && <SurchargePolicy order={order} bookingConfig={bookingConfs.data} />}</Box>
        </Stack>
        <Stack direction="column" spacing={2} flex={1} mt={isMobile ? 3 : 0} width="100%">
          <Box sx={{ flex: 1 }}>
            {receipt.isLoading ? (
              <PurchaseReceiptSkeleton />
            ) : (
              <PurchaseReceipt receipt={receipt.data} isVisit={isVisit} order={order} />
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}
